// API Client — T055
const API_BASE = import.meta.env.VITE_API_BASE || '/api';

function getToken(): string | null {
  return localStorage.getItem('cm_token');
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    // Try refresh
    const refreshed = await refreshToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${getToken()}`;
      const retry = await fetch(`${API_BASE}${path}`, { ...options, headers });
      if (!retry.ok) throw new ApiError(retry.status, await retry.text());
      if (retry.status === 204) return undefined as T;
      return retry.json();
    }
    localStorage.removeItem('cm_token');
    window.location.href = '/login';
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Auth
export async function login(email: string, password: string): Promise<{ token: string; user: any }> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    credentials: 'include',
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  const data = await res.json();
  localStorage.setItem('cm_token', data.token);
  return data;
}

export async function refreshToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = await res.json();
    localStorage.setItem('cm_token', data.token);
    return true;
  } catch {
    return false;
  }
}

export function logout() {
  localStorage.removeItem('cm_token');
  window.location.href = '/login';
}

export function oauthLogin() {
  window.location.href = `${API_BASE}/auth/oauth/google`;
}

// Monitors
export interface Monitor {
  id: string;
  name: string;
  url: string;
  method: string;
  interval: number;
  maxRetries: number;
  state: 'UP' | 'DEGRADED' | 'DOWN';
  muted: boolean;
  consecutiveFailures: number;
  lastPollAt?: string;
  knownIPs?: string[];
  cname?: string;
  lastDnsMutationAt?: string;
  dnsMutated?: boolean;
  uptimePercent?: number;
  hostingProvider?: string;
  providerAccount?: string;
  client?: string;
  recipients: { type: 'email' | 'sms'; address: string }[];
  headers?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages?: number;
}

export interface StatusCounts {
  UP: number;
  DEGRADED: number;
  DOWN: number;
}

export interface FilterOptions {
  providers: string[];
  accounts: string[];
  clients: string[];
}

export async function getMonitors(params?: Record<string, string>): Promise<{ data: Monitor[]; pagination: PaginationInfo; statusCounts?: StatusCounts; filterOptions?: FilterOptions }> {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiFetch(`/monitors${query}`);
}

export async function getMonitor(id: string): Promise<Monitor> {
  return apiFetch(`/monitors/${id}`);
}

export async function createMonitor(data: Partial<Monitor>): Promise<Monitor> {
  return apiFetch('/monitors', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateMonitor(id: string, data: Partial<Monitor>): Promise<Monitor> {
  return apiFetch(`/monitors/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteMonitor(id: string): Promise<void> {
  return apiFetch(`/monitors/${id}`, { method: 'DELETE' });
}

export async function toggleMute(id: string, muted: boolean): Promise<Monitor> {
  return apiFetch(`/monitors/${id}/mute`, { method: 'PATCH', body: JSON.stringify({ muted }) });
}

export async function checkMonitorNow(id: string): Promise<{ status: string; monitorId: string }> {
  return apiFetch(`/monitors/${id}/check`, { method: 'POST' });
}

// Metrics
export interface Metric {
  id: string;
  monitor_id: string;
  responseTimeMs: number;
  statusCode: number;
  state: string;
  timestamp: string;
}

export async function getMetrics(monitorId: string, params?: Record<string, string>): Promise<{ data: Metric[] }> {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiFetch(`/monitors/${monitorId}/metrics${query}`);
}

// Incidents
export interface Incident {
  id: string;
  monitorId: string;
  state: 'OPEN' | 'CLOSED';
  openedAt: string;
  closedAt?: string;
  resolvedAt?: string;
  alerted: boolean;
  alertedAt?: string;
  recoveryAlertSent?: boolean;
}

export async function getIncidents(monitorId: string, state?: string): Promise<{ data: Incident[] }> {
  const query = state ? `?state=${state}` : '';
  return apiFetch(`/monitors/${monitorId}/incidents${query}`);
}

// Settings
export interface Settings {
  id: string;
  retentionDays: number;
  appTitle: string;
  updatedAt: string;
  updatedBy: string;
}

export async function getSettings(): Promise<Settings> {
  return apiFetch('/settings');
}

export async function updateSettings(data: { retentionDays: number; confirmUnlimited?: boolean; appTitle?: string }): Promise<Settings> {
  return apiFetch('/settings', { method: 'PUT', body: JSON.stringify(data) });
}

export async function sendTestEmail(to: string): Promise<{ message: string; to: string }> {
  return apiFetch('/settings/test-email', { method: 'POST', body: JSON.stringify({ to }) });
}

// Users
export interface User {
  id: string;
  email: string;
  role: 'Admin' | 'User';
  authMethod: 'local' | 'oauth';
  oauthProvider?: string;
  lastLoginAt?: string;
  createdAt: string;
}

export async function getUsers(params?: Record<string, string>): Promise<{ data: User[] }> {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return apiFetch(`/users${query}`);
}

export async function updateUser(id: string, data: { role: string }): Promise<User> {
  return apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteUser(id: string): Promise<void> {
  return apiFetch(`/users/${id}`, { method: 'DELETE' });
}

// Import/Export
export async function exportMonitors(): Promise<string> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/monitors/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.text();
}

export async function importMonitors(csvContent: string): Promise<{ created: number; updated: number; failed: number; errors: any[] }> {
  return apiFetch('/monitors/import', {
    method: 'POST',
    headers: { 'Content-Type': 'text/csv' },
    body: csvContent,
  });
}

// Status Pages
export interface StatusPage {
  id: string;
  title: string;
  slug: string;
  monitorIds: string[];
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export async function getStatusPages(): Promise<{ data: StatusPage[] }> {
  return apiFetch('/status-pages');
}

export async function getStatusPage(id: string): Promise<StatusPage> {
  return apiFetch(`/status-pages/${id}`);
}

export async function createStatusPage(data: Partial<StatusPage>): Promise<StatusPage> {
  return apiFetch('/status-pages', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateStatusPage(id: string, data: Partial<StatusPage>): Promise<StatusPage> {
  return apiFetch(`/status-pages/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteStatusPage(id: string): Promise<void> {
  return apiFetch(`/status-pages/${id}`, { method: 'DELETE' });
}

// Public Status
export interface PublicStatus {
  title: string;
  slug: string;
  overallStatus: string;
  monitors: PublicMonitor[];
  updatedAt: string;
}

export interface PublicMonitor {
  id: string;
  name: string;
  url: string;
  state: string;
  uptimePercent?: number;
  lastCheckedAt?: string;
  currentIncident?: { id: string; state: string; openedAt: string } | null;
}

export interface PublicMonitorDetail {
  id: string;
  name: string;
  url: string;
  state: string;
  lastCheckedAt?: string;
  uptimePercent?: number;
  metrics?: { timestamp: string; responseTimeMs: number; statusCode: number }[];
  recentIncidents: { id: string; state: string; openedAt: string; closedAt?: string }[];
}

export async function getPublicStatus(slug: string): Promise<PublicStatus> {
  const res = await fetch(`${API_BASE}/public/status/${slug}`);
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

export async function getPublicMonitorDetail(slug: string, monitorId: string): Promise<PublicMonitorDetail> {
  const res = await fetch(`${API_BASE}/public/status/${slug}/monitors/${monitorId}`);
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

// Health
export async function getHealth(): Promise<{ status: string; uptime: number; version: string }> {
  const res = await fetch(`${API_BASE}/health`);
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}
